import React from 'react';
import AppBase from '@atlassian/jira-app-base';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import type { SecuritySettingsResponse } from '@atlassian/jira-servicedesk-customer-permissions/src/services/types';
import { CustomerPermissionsApp } from '@atlassian/jira-servicedesk-customer-permissions/src/ui/index.tsx';
import { ServiceDeskDocumentTitle } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/document-title/index.tsx';
import { toProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import AppContainer from '@atlassian/jira-spa-apps-project-settings-common/src/components/app-container';
import ServicedeskChannelsBreadcrumb from '@atlassian/jira-spa-apps-project-settings-common/src/components/service-desk-channels-breadcrumb';
import type { RouteResourceError } from '@atlassian/react-resource-router';
import { CustomerPermissionsPage } from './styled';

type CustomerPermissionsState = {
	data: SecuritySettingsResponse | null;
	loading: boolean;
	error: RouteResourceError | null;
	update: (arg1: SecuritySettingsResponse) => void;
};

export type Props = {
	projectContext: ProjectContext | null;
	customerPermissionsState: CustomerPermissionsState;
};

export const ServiceDeskCustomerPermissions = ({
	projectContext,
	customerPermissionsState,
}: Props) => {
	if (customerPermissionsState.loading || !projectContext) {
		return <Spinner />;
	}

	if (customerPermissionsState.error) {
		// Trigger error boundary when fetching data fails, since we can't display anything meaningful
		throw customerPermissionsState.error;
	}

	if (customerPermissionsState.data == null) {
		// This is mostly a type guard for below, but if we do somehow get here, something went wrong, and we have no data that explains why :(
		throw new Error('Unexpected error occurred');
	}

	const { projectKey, projectName, simplified } = projectContext;

	const customerPermissionsApp = (
		<AppBase id="customer-permissions">
			<CustomerPermissionsPage>
				<CustomerPermissionsApp
					source={customerPermissionsState.data}
					onResourceUpdate={customerPermissionsState.update}
					projectKey={toProjectKey(projectKey)}
					isProjectSimplified={simplified}
				/>
			</CustomerPermissionsPage>
		</AppBase>
	);

	return (
		<AppContainer baseUrl="" projectKey={projectKey}>
			<ServiceDeskDocumentTitle projectName={projectName} section="customerPermissions" />
			{simplified && !fg('jsm_simplified_project_settings_navigation') ? (
				<ServicedeskChannelsBreadcrumb baseUrl="" projectKey={projectKey}>
					{customerPermissionsApp}
				</ServicedeskChannelsBreadcrumb>
			) : (
				customerPermissionsApp
			)}
		</AppContainer>
	);
};
